/**
 * @typedef {"expensive"|"noTime"|"technicalIssues"|"lacksFeatures"|"other"} CancellationReason
 */

/**
 * Cancellation reason(s) for the first dropdown in the cancel account flow.
 * @type {Record<CancellationReason, string | string[]>}
 */
export const CancelQuestionsDropdown1 = {
	expensive: 'The service is too expensive.',
	noTime: 'I didn’t have time to try out the service.',
	technicalIssues: [
		'I’ve experienced technical issues.',
		'I have unresolved technical issues.',
		'Unresolved technical issues',
	],
	lacksFeatures: [
		'Stamps.com lacks the features that I need.',
		'The service lacks features I need.',
	],
	other: 'Other (Specify)',
}; // If these are changed, need to update them in CancelAccount.component.js

/**
 * Simplify up a cancellation reason string.
 * either from chargebee/brightback with _[\d+] suffix or from enum without suffix.
 * @param {string} reason
 * @returns {string}
 */
export const simplifyReason = reason =>
	reason
		.replaceAll('_', ' ')
		.substring(
			0,
			reason.lastIndexOf('_') > 0 ? reason.lastIndexOf('_') : reason.length
		)
		.toLowerCase()
		.replaceAll(/[^a-z0-9 ]/g, '');

/**
 * Get CancellationReason from reason string.
 * @param {string} simpleReason - Should be [^a-z0-9 ]+.
 * @returns {CancellationReason | undefined} The cancellation reason or undefined if not found.
 */
export const getCancelQuestionsDropdown1Reason = simpleReason =>
	Object.entries(CancelQuestionsDropdown1).find(([, value]) => {
		const values = (Array.isArray(value) ? value : [value]).map(simplifyReason);
		return values.includes(simpleReason);
	})?.[0] || 'other';

/**
 * Get canonical question for a cancellation reason.
 * @param {CancellationReason} responseCode
 * @returns {string | undefined} The canonical response for the given cancellation reason.
 */
export const getCancelQuestionsDropdown1CanonicalResponse = responseCode => {
	const reasonOrReasonArray = CancelQuestionsDropdown1[responseCode];
	return Array.isArray(reasonOrReasonArray)
		? reasonOrReasonArray[0]
		: reasonOrReasonArray;
};

/**
 * Canonical catamorphism of CancelQuestionsDropdown1.
 * @type {Record<CancellationReason, string>}
 */
export const CancelQuestionsDropdown1DropdownOptions = Object.fromEntries(
	Object.entries(CancelQuestionsDropdown1).map(([key, value]) => {
		// CancelQuestionsDropdown1 has some values that are arrays, canonicalize to the first one.
		if (Array.isArray(value)) {
			return [key, value[0]];
		}
		return [key, value];
	})
);

export const CancelQuestionsDropdown2 = {
	retailLocation: 'Retail Location (ATM, Costco, Grocery Store)',
	postOffice: 'Local Post Office',
	usps: 'USPS Online',
	marketplace: 'Online Marketplace', // exclude on Retention site side. will infer on our side
	otherCarrier: 'Other Carrier',
	postage: 'Postage Meter',
	stamps: 'Stamps.com',
	multiCarrier: 'Multi-Carrier Software', // exclude on Retention site side. will infer on our side
	closed: 'Business Closed/Business Declined',
	other: 'Other (Specify)', // this other text is obfuscated on the brigtback side.
};

export const CancelQuestionsDropdown3 = {
	shipstation: 'ShipStation',
	shipworks: 'ShipWorks',
	other: 'Other (Specify)',
};

export const CancelQuestionsDropdown4 = {
	amazon: 'Amazon',
	eBay: 'eBay',
	etsy: 'Etsy',
	marketplaces: 'Multiple Marketplaces',
	other: 'Other (Specify)',
};

/*
 NewMailingMethod Mapping
 0 - Other
 1 - Alternate Internet Postage
 2 - Postage Meter
 3 - Regular Stamps
*/
export const NewMailingMethod = {
	amazon: 0,
	eBay: 0,
	etsy: 0,
	marketplaces: 0,
	other: 0,
	shipstation: 1,
	shipworks: 1,
};

export const CancelReasonForRetention = {
	expensive: 1473,
	noTime: 1478,
	technicalIssues: 1476,
	lacksFeatures: 1479,
	other: 1493,
};
